import { withDevtools } from '@ngrx-toolkit/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { DataScope, IqBoostPromptCategory } from '@maximizer/ai/data-access';
import { secureRandomId } from '@maximizer/outlook/shared/data-access';
import { AiErrorType } from '../utils/ai-error-classifier';
import {
  IqBoostConfirmCreateCRMAction,
  IqBoostPage,
  IqChatMessage,
  IqPromptSelection,
} from '../models/iq-boost';

type IqBoostState = {
  iqBoostPage: IqBoostPage;
  currentPrompt: string | null;
  currentPromptSelection: IqPromptSelection | null;
  loading: boolean;
  promptCategories: IqBoostPromptCategory[];
  emailAssistPrompts: IqPromptSelection[];
  summaryResponse: string | null;
  summaryFeedbackGiven: boolean;
  chatPrompt: string | null;
  chatResponse: string | null;
  chatHistory: IqChatMessage[];
  chatFeedbackGiven: boolean;
  conversationId: string | null;
  recentPrompts: string[];
  dataScope: DataScope | null;
  confirmCreateCRMAction: IqBoostConfirmCreateCRMAction | null;
  confirmCreateCRMActionFor: string;
  confirmCreateCRMActionForLoading: boolean;
  firstTimeLaunch: boolean | null;
  error: AiErrorType | null;
};

const initialState: IqBoostState = {
  iqBoostPage: 'main',
  currentPrompt: null,
  currentPromptSelection: null,
  loading: false,
  promptCategories: [],
  emailAssistPrompts: [],
  summaryResponse: null,
  summaryFeedbackGiven: false,
  chatPrompt: null,
  chatResponse: null,
  chatHistory: [],
  chatFeedbackGiven: false,
  conversationId: null,
  recentPrompts: [],
  dataScope: null,
  confirmCreateCRMAction: null,
  confirmCreateCRMActionFor: '',
  confirmCreateCRMActionForLoading: false,
  firstTimeLaunch: null,
  error: null,
};

export const OutlookIqBoostStore = signalStore(
  withDevtools('iq-boost'),
  withState({
    ...initialState,
  }),
  withMethods((store) => ({
    setIqBoostPage(iqBoostPage: IqBoostPage) {
      patchState(store, () => ({ iqBoostPage }));
    },
    setCurrentPrompt(currentPrompt: string) {
      patchState(store, () => ({ currentPrompt }));
    },
    setCurrentPromptSelection(currentPromptSelection: IqPromptSelection) {
      patchState(store, () => ({ currentPromptSelection }));
    },
    setLoading(loading: boolean) {
      patchState(store, () => ({ loading }));
    },
    setPromptCategories(promptCategories: IqBoostPromptCategory[]) {
      patchState(store, () => ({ promptCategories }));
    },
    setEmailAssistPrompts(emailAssistPrompts: IqPromptSelection[]) {
      patchState(store, () => ({ emailAssistPrompts }));
    },
    // The server keeps chat history per conversationId; a new id starts a clean thread.
    startConversation(entityKey: string, promptName: string): string {
      const existing = store.conversationId();
      if (existing) return existing;
      const conversationId = `${entityKey}|${promptName}|outlook-chat|${secureRandomId()}`;
      patchState(store, () => ({ conversationId }));
      return conversationId;
    },
    setSummaryResponse(summaryResponse: string) {
      patchState(store, () => ({ summaryResponse }));
    },
    clearSummaryResponse() {
      patchState(store, () => ({
        summaryResponse: null,
        summaryFeedbackGiven: false,
        error: null,
      }));
    },
    setSummaryFeedbackGiven(summaryFeedbackGiven: boolean) {
      patchState(store, () => ({ summaryFeedbackGiven }));
    },
    setChatPrompt(chatPrompt: string) {
      patchState(store, () => ({ chatPrompt }));
    },
    setChatResponse(chatResponse: string) {
      patchState(store, () => ({ chatResponse }));
    },
    clearChatResponse() {
      patchState(store, () => ({
        chatResponse: null,
        chatFeedbackGiven: false,
      }));
    },
    setChatFeedbackGiven(chatFeedbackGiven: boolean) {
      patchState(store, () => ({ chatFeedbackGiven }));
    },
    addChatMessage(message: IqChatMessage) {
      patchState(store, (state) => ({
        chatHistory: [...state.chatHistory, message],
      }));
    },
    clearChatHistory() {
      patchState(store, () => ({
        chatHistory: [],
        chatFeedbackGiven: false,
        conversationId: null,
        error: null,
      }));
    },
    clearChatPrompt() {
      patchState(store, () => ({ chatPrompt: null }));
    },
    setRecentPrompts(recentPrompts: string[]) {
      patchState(store, () => ({ recentPrompts }));
    },
    setDataScope(dataScope: DataScope) {
      patchState(store, () => ({ dataScope }));
    },
    setConfirmCreateCRMAction(
      confirmCreateCRMAction: IqBoostConfirmCreateCRMAction,
    ) {
      patchState(store, () => ({ confirmCreateCRMAction }));
    },
    setConfirmCreateCRMActionFor(confirmCreateCRMActionFor: string) {
      patchState(store, () => ({ confirmCreateCRMActionFor }));
    },
    setConfirmCreateCRMActionForLoading(
      confirmCreateCRMActionForLoading: boolean,
    ) {
      patchState(store, () => ({ confirmCreateCRMActionForLoading }));
    },
    clearConfirmCreateCRMAction() {
      patchState(store, () => ({
        confirmCreateCRMAction: null,
        confirmCreateCRMActionFor: '',
        confirmCreateCRMActionForLoading: false,
      }));
    },
    setFirstTimeLaunch(firstTimeLaunch: boolean) {
      patchState(store, () => ({ firstTimeLaunch }));
    },
    setError(error: AiErrorType | null) {
      patchState(store, () => ({ error }));
    },
    clearError() {
      patchState(store, () => ({ error: null }));
    },
  })),
);

export type OutlookIqBoostStore = InstanceType<typeof OutlookIqBoostStore>;
